//@ts-nocheck
import React, { useState, useEffect } from 'react';
import { Table, PencilIcon, Button, notification } from '@gamesb42/ui-kit';
import { useParams } from 'react-router-dom';
import { mapOnboardingData } from 'helpers/mappers/onboardingData';
import { EditMonetizationModal } from './EditMonetizationModal';
import { EditUaSpendModal } from './EditUaSpendModal';
import { EditMmpModal } from './EditMmpModal';
import { FillQuestionnaireModal } from './FillQuestionnaireModal';
import baseRequest from 'queries/baseRequest';

import s from './styles.module.scss';

function NoQuestionnaireState({ onFill }: { onFill?: () => void }) {
  return (
    <div className={s.emptyState}>
      <span className={s.emptyStateText}>There are no data</span>
      <Button type="default" icon={<PencilIcon />} onClick={onFill}>
        Fill in the Questionnaire
      </Button>
    </div>
  );
}

export function AppGameInfo({ rows, getOnboardingData }: { rows: any[]; getOnboardingData: () => any }) {
  const [renderrows, setRenderRows] = useState(rows);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [rawData, setRawData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [hasNoQuestionnaire, setHasNoQuestionnaire] = useState(false);
  const [isFillModalOpen, setIsFillModalOpen] = useState(false);
  const { companyId } = useParams();

  const loadOnboardingData = () => {
    getOnboardingData()
      .then((data: any) => {
        setRawData(data);
        const result = mapOnboardingData(data);
        setRenderRows(result.rows);
        setHasNoQuestionnaire(false);
      })
      .catch(() => {
        setHasNoQuestionnaire(true);
      });
  };

  useEffect(() => {
    loadOnboardingData();
  }, []);

  const handleEditClick = (record: any) => {
    // Определяем, какое поле редактируется по имени
    const fieldName = record.name?.props?.children;
    
    if (fieldName === 'Monetization model of your app/game?') {
      setEditingField('monetization');
      setIsModalOpen(true);
    } else if (fieldName === 'UA spends sources:') {
      setEditingField('uaSpend');
      setIsModalOpen(true);
    } else if (fieldName === 'Your current MMP (tracking service):') {
      setEditingField('mmp');
      setIsModalOpen(true);
    }
  };

  const handleSaveMonetization = async (data: any) => {
    setLoading(true);
    
    const sca = window.ENV?.REACT_APP_SCA_URL || process.env.REACT_APP_SCA_URL;
    
    try {
      // Формируем массив monetization - только выбранное значение или пустой массив
      const monetizationArray = data.monetization ? [data.monetization] : [];
      
      // Если есть комментарий, добавляем его как "Other: комментарий"
      if (data.otherComment && data.otherComment.trim()) {
        monetizationArray.push(`Other: ${data.otherComment.trim()}`);
      }
      
      await baseRequest({
        path: `${sca}/v1/onboarding/${companyId}`,
        options: {
          method: 'PATCH',
          body: JSON.stringify({
            monetization: monetizationArray,
          }),
        },
      });
      
      notification.success({
        message: 'Monetization model has been updated successfully',
        theme: 'dark',
        placement: 'bottomLeft',
      });
      
      // Перезагружаем данные
      loadOnboardingData();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating monetization:', error);
      notification.error({
        message: 'Failed to update monetization model. Please try again.',
        theme: 'dark',
        placement: 'bottomLeft',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUaSpend = async (data: any) => {
    setLoading(true);
    
    const sca = window.ENV?.REACT_APP_SCA_URL || process.env.REACT_APP_SCA_URL;
    
    try {
      await baseRequest({
        path: `${sca}/v1/onboarding/${companyId}`,
        options: {
          method: 'PATCH',
          body: JSON.stringify({
            uaSpend: data.uaSpend,
          }),
        },
      });
      
      notification.success({
        message: 'UA spends sources has been updated successfully',
        theme: 'dark',
        placement: 'bottomLeft',
      });
      
      // Перезагружаем данные
      loadOnboardingData();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating UA spend:', error);
      notification.error({
        message: 'Failed to update UA spends sources. Please try again.',
        theme: 'dark',
        placement: 'bottomLeft',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMmp = async (data: any) => {
    setLoading(true);
    
    const sca = window.ENV?.REACT_APP_SCA_URL || process.env.REACT_APP_SCA_URL;
    
    try {
      await baseRequest({
        path: `${sca}/v1/onboarding/${companyId}`,
        options: {
          method: 'PATCH',
          body: JSON.stringify({
            mmp: data.mmp,
          }),
        },
      });
      
      notification.success({
        message: 'MMP (tracking service) has been updated successfully',
        theme: 'dark',
        placement: 'bottomLeft',
      });
      
      // Перезагружаем данные
      loadOnboardingData();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating MMP:', error);
      notification.error({
        message: 'Failed to update MMP (tracking service). Please try again.',
        theme: 'dark',
        placement: 'bottomLeft',
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      dataIndex: 'name',
    },
    {
      dataIndex: 'value',
      render: (value: React.ReactNode) => <div className={s.cellValue}>{value}</div>,
    },
  ];

  if (hasNoQuestionnaire) {
    return (
      <div className={s.wrapper}>
        <NoQuestionnaireState onFill={() => setIsFillModalOpen(true)} />
        <FillQuestionnaireModal
          isOpen={isFillModalOpen}
          onClose={() => setIsFillModalOpen(false)}
          onSuccess={() => {
            setIsFillModalOpen(false);
            setHasNoQuestionnaire(false);
            loadOnboardingData();
          }}
        />
      </div>
    );
  }

  return (
    <div className={s.wrapper}>
      <Table className={s.table} columns={columns} dataSource={renderrows} showHeader={false} scroll={{ y: 'calc(100vh - 280px)' }} />

      <div className={s.changeAnswersRow}>
        <Button type="default" icon={<PencilIcon />} onClick={() => setIsFillModalOpen(true)}>
          Change answers
        </Button>
      </div>

      <FillQuestionnaireModal
        isOpen={isFillModalOpen}
        onClose={() => setIsFillModalOpen(false)}
        initialData={rawData}
        onSuccess={() => {
          setIsFillModalOpen(false);
          loadOnboardingData();
        }}
      />

      <EditMonetizationModal
        isOpen={isModalOpen && editingField === 'monetization'}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveMonetization}
        initialValue={rawData?.app_questionary?.monetization}
        loading={loading}
      />

      <EditUaSpendModal
        isOpen={isModalOpen && editingField === 'uaSpend'}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveUaSpend}
        initialValue={rawData?.app_questionary?.uaSpend}
        loading={loading}
      />

      <EditMmpModal
        isOpen={isModalOpen && editingField === 'mmp'}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveMmp}
        initialValue={rawData?.app_questionary?.mmp}
        loading={loading}
      />
    </div>
  );
}
